import React from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Box } from "@mui/material";
import { BlockEditor } from "./BlockEditor";
import type { ReportBlock } from "../api/types";
import { metricLabel } from "../utils/metricOptions";

/** Short label for a data block — used as narrative generation context. */
const blockContextLabel = (b: ReportBlock): string | null => {
  switch (b.type) {
    case "chart":
      return b.title || metricLabel(b.metric);
    case "execSummary":
      return b.title || "Executive Summary";
    case "breakdown":
      return b.title || "Breakdown";
    case "kpiCards":
      return b.title || "Key Metrics";
    case "correlation":
      return b.title || "Correlation Matrix";
    case "scatter":
      return b.title || "Scatter";
    case "weatherImpact":
      return b.title || "Weather Impact";
    case "aiPanel":
      return b.title || "AI Wellness";
    default:
      return null;
  }
};

interface ReportCanvasProps {
  blocks: ReportBlock[];
  clientId: string | null;
  onChange: (blocks: ReportBlock[]) => void;
  /** Generates Director-targeted narrative; hint = preceding block context. */
  onGenerateNarrative?: (hint?: string) => Promise<string>;
  /** Generates an AI action plan; wired only to action-plan blocks. */
  onGenerateActionPlan?: () => Promise<string[]>;
}

/** One sortable row: wires dnd-kit's transform + drag handle into BlockEditor. */
const SortableBlock: React.FC<{
  block: ReportBlock;
  clientId: string | null;
  onChange: (block: ReportBlock) => void;
  onRemove: () => void;
  onGenerateNarrative?: (hint?: string) => Promise<string>;
  onGenerateActionPlan?: () => Promise<string[]>;
  precedingBlock?: ReportBlock;
}> = ({
  block,
  clientId,
  onChange,
  onRemove,
  onGenerateNarrative,
  onGenerateActionPlan,
  precedingBlock,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  const narrativeHint = precedingBlock ? blockContextLabel(precedingBlock) ?? undefined : undefined;
  const generateNarrativeWithHint =
    block.type === "narrative" && onGenerateNarrative
      ? () => onGenerateNarrative(narrativeHint)
      : undefined;

  return (
    <Box
      ref={setNodeRef}
      sx={{ opacity: isDragging ? 0.5 : 1 }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <BlockEditor
        block={block}
        clientId={clientId}
        onChange={onChange}
        onRemove={onRemove}
        dragHandleProps={{ ...attributes, ...listeners }}
        onGenerateNarrative={generateNarrativeWithHint}
        onGenerateActionPlan={
          block.type === "actionPlan" ? onGenerateActionPlan : undefined
        }
      />
    </Box>
  );
};

/**
 * Drag-and-drop report canvas. Reorders blocks on drop and proxies per-block
 * edits/removals up to the parent, which owns the layout state.
 */
export const ReportCanvas: React.FC<ReportCanvasProps> = ({
  blocks,
  clientId,
  onChange,
  onGenerateNarrative,
  onGenerateActionPlan,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.id === active.id);
    const newIndex = blocks.findIndex((b) => b.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  const updateBlock = (id: string, next: ReportBlock) =>
    onChange(blocks.map((b) => (b.id === id ? next : b)));

  const removeBlock = (id: string) =>
    onChange(blocks.filter((b) => b.id !== id));

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={blocks.map((b) => b.id)}
        strategy={verticalListSortingStrategy}
      >
        {blocks.map((block, i) => (
          <SortableBlock
            key={block.id}
            block={block}
            clientId={clientId}
            onChange={(next) => updateBlock(block.id, next)}
            onRemove={() => removeBlock(block.id)}
            onGenerateNarrative={onGenerateNarrative}
            onGenerateActionPlan={onGenerateActionPlan}
            precedingBlock={i > 0 ? blocks[i - 1] : undefined}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
};
